<script setup lang="ts">
/**
 * FittingEditor — Main fitting editor component
 *
 * Displays: ShipHeader, slot groups (high/med/low/rig), subsystems (T3),
 * additions tabs (drones/fighters/cargo/implants), context menu.
 *
 * Wires: module/drone/cargo/implant/fighter operations through store actions.
 */
import { ref, computed } from 'vue';
import { useFitStore } from '../../stores/fitStore';
import { fetchChargesForModule } from '../../composables/useApi';
import type { FitModule, SlotType, ModuleState, SubsystemSlot, Charge } from '../../../shared/types';
import ShipHeader from './ShipHeader.vue';
import SlotGroup from './SlotGroup.vue';
import SubsystemPanel from './SubsystemPanel.vue';
import AdditionsTabBar from './AdditionsTabBar.vue';
import type { AdditionsTab } from './AdditionsTabBar.vue';
import DronePanel from './DronePanel.vue';
import FighterPanel from './FighterPanel.vue';
import CargoPanel from './CargoPanel.vue';
import ImplantPanel from './ImplantPanel.vue';
import ContextMenu from './ContextMenu.vue';
import type { ContextMenuItem } from './ContextMenu.vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';
import ModuleSelectorDialog from '../modals/ModuleSelectorDialog.vue';
import ChargeSelector from '../modals/ChargeSelector.vue';

const store = useFitStore();

const emit = defineEmits<{
  share: [];
  import: [];
  export: [];
}>();

// --- Additions Tab ---

const additionsTab = ref<AdditionsTab>('drones');

function onAdditionsTabChange(tab: AdditionsTab): void {
  additionsTab.value = tab;
}

// --- Context Menu State ---

const contextMenu = ref<{
  show: boolean;
  x: number;
  y: number;
  module: FitModule | null;
  items: ContextMenuItem[];
  charges: Charge[];
}>({
  show: false,
  x: 0,
  y: 0,
  module: null,
  items: [],
  charges: [],
});

// --- Slot Counts ---

const slotCounts = computed(() => {
  const stats = store.activeStats;
  if (!stats) {
    return { high: 8, med: 8, low: 8, rig: 3 };
  }
  return {
    high: stats.resources.slots.high.total,
    med: stats.resources.slots.med.total,
    low: stats.resources.slots.low.total,
    rig: stats.resources.slots.rig.total,
  };
});

// --- Module Helpers ---

function modulesForSlot(slotType: SlotType): FitModule[] {
  const fit = store.activeFit;
  if (!fit) return [];
  return fit.modules
    .filter(m => m.slotType === slotType)
    .sort((a, b) => a.slotPosition - b.slotPosition);
}

// Is this a T3 cruiser? (has subsystem slots)
const isT3Cruiser = computed(() => {
  const fit = store.activeFit;
  if (!fit) return false;
  return fit.subsystems.length > 0 || fit.modules.some(m => m.slotType === 'subsystem');
});

// Is this a carrier/supercarrier?
const isCarrier = computed(() => store.activeFit?.isCarrier ?? false);

// --- Charge Selector ---

const chargeSelector = ref<{
  visible: boolean;
  moduleId: number;
  moduleTypeID: number;
  currentChargeTypeID: number | null;
}>({
  visible: false,
  moduleId: 0,
  moduleTypeID: 0,
  currentChargeTypeID: null,
});

async function handleChargeSelected(chargeTypeID: number): Promise<void> {
  chargeSelector.value.visible = false;
  await store.setModuleCharge(chargeSelector.value.moduleId, chargeTypeID);
}

async function handleChargeUnloaded(): Promise<void> {
  chargeSelector.value.visible = false;
  await store.setModuleCharge(chargeSelector.value.moduleId, null);
}

// --- Module Selector ---

const moduleSelector = ref<{
  visible: boolean;
  slotType: SlotType;
  slotPosition: number;
}>({
  visible: false,
  slotType: 'high',
  slotPosition: 0,
});

function handleClickEmpty(slotType: SlotType, slotPosition: number): void {
  moduleSelector.value = { visible: true, slotType, slotPosition };
}

async function handleModuleSelected(payload: { typeID: number; typeName: string }): Promise<void> {
  const { slotType, slotPosition } = moduleSelector.value;
  moduleSelector.value.visible = false;
  await store.addModuleToFit(payload.typeID, payload.typeName, slotType, slotPosition);
}

// --- Event Handlers ---

async function handleSetState(moduleId: number, state: ModuleState): Promise<void> {
  await store.setModuleStateFit(moduleId, state);
}

async function handleRemoveModule(moduleId: number): Promise<void> {
  await store.removeModuleFromFit(moduleId);
}

async function handleDrop(slotType: SlotType, slotPosition: number, typeID: number, typeName: string): Promise<void> {
  await store.addModuleToFit(typeID, typeName, slotType, slotPosition);
}

// --- Context Menu ---

async function handleContextMenu(e: MouseEvent, mod: FitModule): Promise<void> {
  const items: ContextMenuItem[] = [];

  // State controls
  const states: { state: ModuleState; label: string }[] = [
    { state: 'offline', label: 'Offline' },
    { state: 'online', label: 'Online' },
    { state: 'active', label: 'Active' },
    { state: 'overheated', label: 'Overheat' },
  ];

  for (const s of states) {
    const disabled = (s.state === 'active' && !mod.canActivate)
      || (s.state === 'overheated' && !mod.canOverheat);
    items.push({
      label: s.label,
      action: `state:${s.state}`,
      active: mod.state === s.state,
      disabled,
    });
  }

  items.push({ label: '', action: '', divider: true });

  // Charge options
  items.push({
    label: mod.chargeName ? `Charge: ${mod.chargeName}` : 'Load Charge...',
    action: 'charge',
  });

  if (mod.chargeTypeID) {
    items.push({
      label: 'Unload Charge',
      action: 'unload_charge',
    });
  }

  items.push({ label: '', action: '', divider: true });

  items.push({
    label: 'Remove Module',
    action: 'remove',
  });

  // Fetch charges in the background
  let charges: Charge[] = [];
  try {
    charges = await fetchChargesForModule(mod.typeID);
  } catch { /* ignore */ }

  contextMenu.value = {
    show: true,
    x: e.clientX,
    y: e.clientY,
    module: mod,
    items,
    charges,
  };
}

async function handleContextAction(action: string): Promise<void> {
  const mod = contextMenu.value.module;
  if (!mod) return;

  if (action.startsWith('state:')) {
    const state = action.split(':')[1] as ModuleState;
    await store.setModuleStateFit(mod.id, state);
  } else if (action === 'remove') {
    await store.removeModuleFromFit(mod.id);
  } else if (action === 'unload_charge') {
    await store.setModuleCharge(mod.id, null);
  } else if (action === 'charge') {
    // Open the charge selector modal
    chargeSelector.value = {
      visible: true,
      moduleId: mod.id,
      moduleTypeID: mod.typeID,
      currentChargeTypeID: mod.chargeTypeID ?? null,
    };
    return; // Don't close context menu implicitly — it already closed
  } else if (action.startsWith('charge:')) {
    const chargeTypeID = parseInt(action.split(':')[1], 10);
    await store.setModuleCharge(mod.id, chargeTypeID);
  }
}

// Charge sub-menu: replaces current context menu with charge list
function showChargeMenu(mod: FitModule): void {
  const items: ContextMenuItem[] = contextMenu.value.charges.map(c => ({
    label: c.typeName,
    action: `charge:${c.typeID}`,
    active: mod.chargeTypeID === c.typeID,
  }));

  if (mod.chargeTypeID) {
    items.unshift({
      label: 'Unload Charge',
      action: 'unload_charge',
    });
    items.splice(1, 0, { label: '', action: '', divider: true });
  }

  contextMenu.value.items = items;
}

function closeContextMenu(): void {
  contextMenu.value.show = false;
}

// --- Subsystem handlers ---

function handleSelectSubsystem(slot: SubsystemSlot): void {
  store.setLeftTab('market');
  store.addToast('info', `Select a ${slot} subsystem from the Market browser`);
}

async function handleRemoveSubsystem(id: number): Promise<void> {
  await store.removeSubsystemFromFit(id);
}
</script>

<template>
  <div class="fitting-editor" v-if="store.activeFit">
    <ShipHeader @share="emit('share')" />

    <div class="editor-body" :class="{ readOnly: store.activeFit?.canEdit === false }">
      <!-- Slot groups -->
      <div class="slot-groups">
        <SlotGroup
          slot-type="high"
          :total-slots="slotCounts.high"
          :modules="modulesForSlot('high')"
          :fit="store.activeFit"
          @click-empty="handleClickEmpty"
          @set-state="handleSetState"
          @remove="handleRemoveModule"
          @context-menu="handleContextMenu"
          @drop="handleDrop"
        />

        <SlotGroup
          slot-type="med"
          :total-slots="slotCounts.med"
          :modules="modulesForSlot('med')"
          :fit="store.activeFit"
          @click-empty="handleClickEmpty"
          @set-state="handleSetState"
          @remove="handleRemoveModule"
          @context-menu="handleContextMenu"
          @drop="handleDrop"
        />

        <SlotGroup
          slot-type="low"
          :total-slots="slotCounts.low"
          :modules="modulesForSlot('low')"
          :fit="store.activeFit"
          @click-empty="handleClickEmpty"
          @set-state="handleSetState"
          @remove="handleRemoveModule"
          @context-menu="handleContextMenu"
          @drop="handleDrop"
        />

        <SlotGroup
          slot-type="rig"
          :total-slots="slotCounts.rig"
          :modules="modulesForSlot('rig')"
          :fit="store.activeFit"
          @click-empty="handleClickEmpty"
          @set-state="handleSetState"
          @remove="handleRemoveModule"
          @context-menu="handleContextMenu"
          @drop="handleDrop"
        />

        <!-- T3 Subsystems -->
        <SubsystemPanel
          v-if="isT3Cruiser"
          :subsystems="store.activeFit.subsystems"
          @select-subsystem="handleSelectSubsystem"
          @remove-subsystem="handleRemoveSubsystem"
        />
      </div>

      <!-- Additions panels (drones/fighters/cargo/implants) -->
      <div class="additions-section">
        <AdditionsTabBar
          :is-carrier="isCarrier"
          :drone-count="store.activeFit.drones.length"
          :fighter-count="store.activeFit.fighters.length"
          :cargo-count="store.activeFit.cargo.length"
          :implant-count="store.activeFit.implants.length"
          @update:tab="onAdditionsTabChange"
        />

        <div class="additions-content">
          <DronePanel v-if="additionsTab === 'drones'" />
          <FighterPanel v-else-if="additionsTab === 'fighters'" />
          <CargoPanel v-else-if="additionsTab === 'cargo'" />
          <ImplantPanel v-else-if="additionsTab === 'implants'" />
        </div>
      </div>
    </div>

    <!-- Context Menu -->
    <ContextMenu
      v-if="contextMenu.show"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :items="contextMenu.items"
      @select="handleContextAction"
      @close="closeContextMenu"
    />

    <!-- Charge Selector Modal -->
    <ChargeSelector
      :visible="chargeSelector.visible"
      :module-type-i-d="chargeSelector.moduleTypeID"
      :current-charge-type-i-d="chargeSelector.currentChargeTypeID"
      @select="handleChargeSelected"
      @unload="handleChargeUnloaded"
      @close="chargeSelector.visible = false"
    />

    <!-- Module Selector Modal -->
    <ModuleSelectorDialog
      :visible="moduleSelector.visible"
      :slot-type="moduleSelector.slotType"
      :fit-i-d="store.activeFit?.fitID ?? 0"
      :ship-type-i-d="store.activeFit?.shipTypeID ?? 0"
      @select="handleModuleSelected"
      @close="moduleSelector.visible = false"
    />
  </div>

  <!-- Loading state -->
  <div v-else-if="store.activeTab?.loading" class="empty-editor">
    <LoadingSpinner label="Loading fit..." size="lg" />
  </div>

  <!-- Empty state -->
  <div v-else class="empty-editor">
    <svg width="48" height="48" viewBox="0 0 48 48" class="empty-icon">
      <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" stroke-width="1.5" />
      <line x1="24" y1="16" x2="24" y2="32" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      <line x1="16" y1="24" x2="32" y2="24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
    </svg>
    <span class="empty-title">Select a Ship</span>
    <span class="empty-sub">Choose a ship from the browser to start fitting</span>
  </div>
</template>

<style scoped>
.fitting-editor {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.editor-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px 16px;
}

.editor-body.readOnly { opacity: .72; pointer-events: none; }

.slot-groups {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

/* Additions section */
.additions-section {
  margin: 14px 0 0;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--bg-panel);
}

.additions-content {
  max-height: 280px;
  overflow-y: auto;
}

/* Empty state */
.empty-editor {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 8px;
  color: var(--text-secondary);
}

.empty-icon {
  opacity: 0.3;
  margin-bottom: 8px;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--text-primary);
}

.empty-sub {
  font-size: 0.85rem;
}

/* ─── Responsive ─── */
@media (max-width: 900px) {
  .editor-body {
    padding: 6px 8px 12px;
  }

  .slot-groups {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .additions-section {
    margin-top: 8px;
  }
}
</style>
